// useCanvasTransform — hook for Miro-like zoom/pan on the simulation canvas.
// Computes an SVG viewBox from scale/pan state for crisp vector rendering.

import { useRef, useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectScale,
  selectPan,
  zoomToPoint,
  setPan,
  MIN_SCALE,
  MAX_SCALE,
} from "../../store/slices/canvasSlice.ts";

const ZOOM_SENSITIVITY = 0.001;
const DRAG_THRESHOLD = 3;

function pointerDistance(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pointerMidpoint(a: { x: number; y: number }, b: { x: number; y: number }) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function useCanvasTransform() {
  const dispatch = useDispatch();
  const scale = useSelector(selectScale);
  const pan = useSelector(selectPan);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  // Track container dimensions for viewBox computation
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    };
    update();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dragState = useRef<{
    active: boolean;
    pointerId: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
    moved: boolean;
  } | null>(null);

  // ── Pinch-to-zoom state ──
  const pinchState = useRef<{
    active: boolean;
    pointers: Map<number, { x: number; y: number }>;
    initialDist: number;
    initialScale: number;
    initialPanX: number;
    initialPanY: number;
    initialMid: { x: number; y: number };
  } | null>(null);

  // ── Wheel zoom (centered on cursor) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = el.getBoundingClientRect();
      const pointX = e.clientX - rect.left;
      const pointY = e.clientY - rect.top;

      const delta = -e.deltaY * ZOOM_SENSITIVITY;
      const newScale = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, scale * (1 + delta)),
      );

      dispatch(zoomToPoint({ newScale, pointX, pointY }));
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [dispatch, scale]);

  // ── Drag-to-pan ──
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Track pointer for potential pinch gesture
      if (!pinchState.current) {
        pinchState.current = {
          active: false,
          pointers: new Map(),
          initialDist: 0,
          initialScale: scale,
          initialPanX: pan.x,
          initialPanY: pan.y,
          initialMid: { x: 0, y: 0 },
        };
      }
      pinchState.current.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      // If two fingers are down, start pinch mode
      if (pinchState.current.pointers.size === 2) {
        // Cancel any single-finger drag
        if (dragState.current) {
          containerRef.current?.releasePointerCapture(dragState.current.pointerId);
          dragState.current = null;
        }
        const [a, b] = [...pinchState.current.pointers.values()];
        pinchState.current.active = true;
        pinchState.current.initialDist = pointerDistance(a, b);
        pinchState.current.initialScale = scale;
        pinchState.current.initialPanX = pan.x;
        pinchState.current.initialPanY = pan.y;
        pinchState.current.initialMid = pointerMidpoint(a, b);
        return;
      }

      if (e.button !== 0) return;
      dragState.current = {
        active: true,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        panX: pan.x,
        panY: pan.y,
        moved: false,
      };
    },
    [pan.x, pan.y, scale],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Update pinch pointer tracking
      const ps = pinchState.current;
      if (ps) {
        ps.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // Handle active pinch gesture
        if (ps.active && ps.pointers.size === 2) {
          const [a, b] = [...ps.pointers.values()];
          const currentDist = pointerDistance(a, b);
          const ratio = currentDist / ps.initialDist;
          const newScale = Math.min(
            MAX_SCALE,
            Math.max(MIN_SCALE, ps.initialScale * ratio),
          );

          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const mid = pointerMidpoint(a, b);
            const pointX = mid.x - rect.left;
            const pointY = mid.y - rect.top;
            dispatch(zoomToPoint({ newScale, pointX, pointY }));

            // Pan to follow finger midpoint movement
            const dx = mid.x - ps.initialMid.x;
            const dy = mid.y - ps.initialMid.y;
            dispatch(setPan({ x: ps.initialPanX + dx, y: ps.initialPanY + dy }));
          }
          return;
        }
      }

      const ds = dragState.current;
      if (!ds?.active) return;

      const dx = e.clientX - ds.startX;
      const dy = e.clientY - ds.startY;

      if (
        !ds.moved &&
        Math.abs(dx) < DRAG_THRESHOLD &&
        Math.abs(dy) < DRAG_THRESHOLD
      ) {
        return;
      }

      if (!ds.moved) {
        ds.moved = true;
        containerRef.current?.setPointerCapture(ds.pointerId);
      }

      dispatch(setPan({ x: ds.panX + dx, y: ds.panY + dy }));
    },
    [dispatch],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    // Clean up pinch state
    const ps = pinchState.current;
    if (ps) {
      ps.pointers.delete(e.pointerId);
      if (ps.pointers.size < 2) {
        ps.active = false;
      }
      if (ps.pointers.size === 0) {
        pinchState.current = null;
      }
    }

    const ds = dragState.current;
    if (!ds) return;

    containerRef.current?.releasePointerCapture(e.pointerId);

    // Suppress click after drag so nodes don't accidentally get selected
    if (ds.moved) {
      const el = containerRef.current;
      const suppress = (ev: MouseEvent) => {
        ev.stopPropagation();
      };
      el?.addEventListener("click", suppress, { capture: true, once: true });
    }

    dragState.current = null;
  }, []);

  // viewBox: map screen-space pan/scale to SVG coordinate viewport
  const viewBox =
    containerSize.w > 0
      ? `${-pan.x / scale} ${-pan.y / scale} ${containerSize.w / scale} ${containerSize.h / scale}`
      : undefined;

  return {
    containerRef,
    viewBox,
    containerSize,
    scale,
    pan,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
  };
}
